import {
  Button,
  Container,
  ContentLayout,
  Header,
  Pagination,
} from '@cloudscape-design/components';
import { ShippingSteps } from '@parallel-fluidics/shared/constants';
import CustomsForm, { DefaultCustomsItem } from 'components/Orders/package/customsForm';
import PackageDetail from 'components/Orders/package/packageDetail';
import { DRAFT_NOTIFICATION_ID, usePackage } from 'components/Orders/package/packageProvider';
import PackingSlipForm from 'components/Orders/package/packingSlipForm';
import ParcelForm from 'components/Orders/package/parcelForm';
import PDFPreview from 'components/Orders/package/pdfPreview';
import PDFViewer from 'components/Orders/package/pdfViewer';
import SelectCarrier from 'components/Orders/package/selectCarrier';
import { useShipping } from 'components/Orders/package/shippingProvider';
import { useNotifications } from 'features/notifications';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function CreatePackagePage() {
  const navigate = useNavigate();
  const { removeNotification } = useNotifications();
  const { isInternational, setCustomsItems, handleUpdateParcelNumbers } = useShipping();
  const {
    packages,
    totalPkgNum,
    currentPkgNum,
    setCurrentPkgNum,
  } = usePackage();

  const [step, setStep] = useState(ShippingSteps.PACKING_INFO);

  // The "restored draft" notice only makes sense while editing the packing
  // slip — hide it once the user moves on to later steps.
  useEffect(() => {
    if (step !== ShippingSteps.PACKING_INFO) {
      removeNotification(DRAFT_NOTIFICATION_ID);
    }
  }, [step, removeNotification]);

  return (
    <ContentLayout
      header={(
        <Header
          actions={(
            <Button
              variant="normal"
              onClick={() => { navigate('/orders'); }}
            >
              See all orders
            </Button>
          )}
        >
          Create Packages
        </Header>
      )}
    >
      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 48px - 104px)' }}>
        <div style={{ flex: 1, width: '50%' }}>
          <div style={{ paddingBottom: 16 }}>
            {step === ShippingSteps.PACKING_INFO && (
              <PackingSlipForm
                onNext={() => {
                  handleUpdateParcelNumbers(packages.length);
                  if (isInternational) {
                    // based on packages -> lineItems -> isChecked
                    const selectedLineItems = packages
                      .flatMap((pkg) => pkg.lineItems)
                      .filter((item) => item.isChecked);

                    const groupedItems = selectedLineItems?.reduce((acc, item) => {
                      if (!acc[item.id]) {
                        return {
                          ...acc,
                          [item.id]: {
                            ...DefaultCustomsItem,
                            lineItemId: item.id,
                            quantity: item.quantityShipped,
                          },
                        };
                      }
                      return {
                        ...acc,
                        [item.id]: {
                          ...acc[item.id],
                          quantity: acc[item.id].quantity + item.quantityShipped,
                        },
                      };
                    }, {});

                    const newCustomsItems = Object.values(groupedItems);
                    setCustomsItems(newCustomsItems);
                  }
                  setStep(isInternational ? ShippingSteps.CUSTOMS : ShippingSteps.PARCEL);
                }}
              />
            )}
            {step === ShippingSteps.CUSTOMS && (
              <CustomsForm
                // disable customs item add or remove if more than 1 packages
                disableItemEdit={packages.length > 1}
                onPrev={() => setStep(ShippingSteps.PACKING_INFO)}
                onNext={() => setStep(ShippingSteps.PARCEL)}
              />
            )}
            {step === ShippingSteps.PARCEL && (
              <ParcelForm
                packagesInfo={isInternational ? packages : []}
                onPrev={() => setStep(isInternational
                  ? ShippingSteps.CUSTOMS
                  : ShippingSteps.PACKING_INFO)}
                onNext={() => setStep(ShippingSteps.SELECT_RATE)}
              />
            )}
            {step === ShippingSteps.SELECT_RATE && (
              <SelectCarrier
                onPrev={() => setStep(ShippingSteps.PARCEL)}
                onNext={() => setStep(ShippingSteps.CONFIRM)}
              />
            )}
            {step === ShippingSteps.CONFIRM && (
              <PackageDetail />
            )}
          </div>
        </div>

        <div style={{ flex: 1, width: '50%' }}>
          <Container
            header={(
              <Header
                actions={totalPkgNum > 1 && (
                  <Pagination
                    currentPageIndex={currentPkgNum}
                    pagesCount={totalPkgNum}
                    onChange={({ detail }) => {
                      setCurrentPkgNum(detail.currentPageIndex);
                    }}
                  />
                )}
              >
                Preview
              </Header>
          )}
            fitHeight
          >
            {step === ShippingSteps.CONFIRM ? (
              <PDFViewer />
            ) : (
              <PDFPreview />
            )}
          </Container>
        </div>
      </div>
    </ContentLayout>
  );
}
